import { useEffect } from 'react';

/**
 * Custom hook to inject JSON-LD Schema Markup into document head
 * This bypasses react-helmet-async's known issues with script children
 * 
 * @param {Object[]} schemas - Array of schema objects to inject
 * @param {string} pageId - Unique identifier for this page's schemas
 */
export function useSchemaMarkup(schemas, pageId) {
  useEffect(() => {
    if (!schemas || schemas.length === 0) return;

    // Create unique IDs for each script
    const scriptIds = schemas.map((_, index) => `schema-${pageId}-${index}`);
    
    // Remove any existing scripts for this page
    scriptIds.forEach(id => {
      const existing = document.getElementById(id);
      if (existing) {
        existing.remove();
      }
    });

    // Inject new scripts
    const createdScripts = schemas.map((schema, index) => {
      if (!schema) return null;
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = scriptIds[index];
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      return script;
    }).filter(Boolean);

    // Cleanup on unmount
    return () => {
      createdScripts.forEach(script => {
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [schemas, pageId]);
}

/**
 * Helper to generate all schemas for a service page
 */
export function generateServicePageSchemas({
  localBusinessSchema,
  serviceSchema,
  breadcrumbSchema,
  faqSchema
}) {
  return [
    localBusinessSchema,
    serviceSchema,
    breadcrumbSchema,
    faqSchema
  ].filter(Boolean);
}
